import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ValidationError } from 'yup';
import { IFile } from '~/services/modules/contract/types';
import useStore from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';
import { ContractFlowHookResult, FilesError, FilesState, TypeFile, UploadFilesResponse } from './types';

export const useStandardFlow = (): ContractFlowHookResult => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [dataLoaded] = useState(true); // No fluxo padrão, sempre começamos com dataLoaded=true
  const [errors, setErrors] = useState<FilesError>({} as FilesError);
  const [hasVoucherError, setHasVoucherError] = useState(false);
  const [submitError, setSubmitError] = useState<{
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
  } | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<{
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
  } | null>(null);
  const [files, setFiles] = useState<FilesState>({
    invoice: null,
    overview: null,
    serial_number_photo: null,
    ratchets_group: null,
    other: null
  });

  const {
    contract_id,
    contractAsyncPostFiles,
    contractAsyncGetVoucher,
    voucher,
    quoteHasInvoice,
    insuredHasInvoice,
    isNew,
    product,
    productId
  } = useStore((store) => ({
    contract_id: store.contract_id,
    contractAsyncPostFiles: store.contractAsyncPostFiles,
    contractAsyncGetVoucher: store.contractAsyncGetVoucher,
    voucher: store.voucher,
    quoteHasInvoice: store.quote.hasInvoice,
    insuredHasInvoice: store.insured_item.has_invoice,
    isNew: store.insured_item.is_new,
    product: store.insured_item.equipment_type_id,
    productId: (store.plan?.products ?? []).map((product) => product.product_id)
  }));

  const hasInvoice = insuredHasInvoice || quoteHasInvoice || false;

  // Lógica para carregar o voucher
  useEffect(() => {
    const orgParam = searchParams.get('org');
    async function fetchVoucherData(contractId: number) {
      setLoading(true);
      try {
        await contractAsyncGetVoucher(contractId, orgParam ?? undefined);
        setLoading(false);
      } catch (error) {
        setHasVoucherError(true);
        setLoading(false);

        const errorContext = logErrorWithContext(error, 'standard_flow_fetch_voucher', {
          contractId,
          hasInvoice
        });

        reportError('Erro ao buscar voucher no fluxo padrão', error, {
          context: 'standard_flow_fetch_voucher',
          contractId,
          hasInvoice,
          errorType: errorContext.errorType
        });
      }
    }

    if (!hasInvoice && contract_id) {
      fetchVoucherData(contract_id as unknown as number);
    }
  }, [hasInvoice, contract_id, contractAsyncGetVoucher, searchParams]);

  // Handler para alteração de arquivo
  const onChangeFile = (key: TypeFile, value: IFile) => {
    setFiles((state) => ({
      ...state,
      [key]: value
    }));
    setErrors((state) => ({ ...state, [key]: false }));
  };

  // Handler para submissão
  const onSubmit = async () => {
    if (contract_id) {
      try {
        setLoading(true);
        setSubmitError(null);
        setSubmitSuccess(null);

        const orgParam = searchParams.get('org');
        const data: UploadFilesResponse = await contractAsyncPostFiles(contract_id, files, orgParam ?? undefined);

        const contract_hash_id = searchParams.get('cid');
        const source = searchParams.get('source');
        const params = new URLSearchParams();
        if (contract_hash_id) params.set('cid', contract_hash_id);
        if (source) params.set('source', source);
        if (orgParam) params.set('org', orgParam);

        if (data.data.is_recurring_moderation) {
          setLoading(false);
          navigate(`/contract/download?${params.toString()}`);
        } else {
          setLoading(false);
          navigate(`/contract/emergency?${params.toString()}`);
        }
      } catch (error: any) {
        setLoading(false);
        if (error instanceof ValidationError) {
          const fieldsToCheck: TypeFile[] = [
            'invoice',
            'overview',
            'serial_number_photo',
            'ratchets_group',
            'right_side_overview',
            'left_side_overview',
            'front_wheel',
            'rear_wheel',
            'front',
            'front_gear_shift',
            'rear_gear_shift',
            'other'
          ];
          const fieldsError = error.errors;
          const newErrors = {} as FilesError;

          fieldsToCheck.forEach((field) => {
            newErrors[field] = fieldsError.includes(field);
          });

          setErrors(newErrors);
        } else {
          const errorContext = logErrorWithContext(error, 'standard_flow_upload_files', {
            contractId: contract_id,
            filesCount: Object.keys(files).length,
            hasNotifications: error.isAxiosError && !!error.response?.data?.notifications
          });

          const errorResponse = getErrorMessage(error);

          setSubmitError(errorResponse);

          reportError(`Erro ao processar arquivos no fluxo padrão: ${errorResponse.title}`, error, {
            context: 'standard_flow_upload_files',
            contractId: contract_id,
            errorType: errorContext.errorType,
            filesCount: Object.keys(files).length,
            originalNotifications: error.isAxiosError ? error.response?.data?.notifications : undefined
          });
        }
      }
    } else {
      setSubmitError({
        title: 'Não foi possível processar os arquivos',
        message: 'Nenhum contrato foi encontrado',
        type: 'warning'
      });
    }
  };

  return {
    loading,
    dataLoaded,
    hasVoucherError,
    files,
    errors,
    hasInvoice,
    isNew,
    product,
    productId,
    voucher,
    isEmailMode: false,
    documentUrls: {},
    submitError,
    submitSuccess,
    onChangeFile,
    onSubmit
  };
};
